import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MessageCircle, Send, ArrowLeft, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, STATUS_LABEL } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { StarRating } from "@/components/StarRating";
import { timeAgo } from "@/components/PostCard";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_STYLE = {
  connected: "bg-gold text-forest-900", in_progress: "bg-terracotta text-white", completed: "bg-stone-200 text-forest-700",
};

function ConversationList({ conns, activeId, onPick }) {
  return (
    <div className="bg-white rounded-2xl border-2 border-border shadow-sm overflow-hidden">
      <div className="p-5 border-b-2 border-border"><h2 className="font-head text-xl font-bold text-forest-900">Conversations</h2></div>
      {conns.length === 0 ? (
        <div className="p-8 text-center text-forest-700">No conversations yet. Connect on a post to start chatting.</div>
      ) : (
        <div className="divide-y divide-border max-h-[70vh] overflow-y-auto">
          {conns.map((c) => (
            <button key={c.connection_id} onClick={() => onPick(c.connection_id)} data-testid={`conv-${c.connection_id}`}
              className={`w-full flex items-center gap-3 p-4 text-left transition-colors ${activeId === c.connection_id ? "bg-cream" : "hover:bg-cream/60"}`}>
              <Avatar user={c.other} size={48} />
              <div className="min-w-0 flex-1">
                <p className="font-bold text-forest-900 truncate">{c.other?.name}</p>
                <p className="text-sm text-forest-500 truncate">{c.post?.title}</p>
              </div>
              {c.unread > 0 && <span className="bg-terracotta text-white text-xs font-bold rounded-full min-w-6 h-6 px-1.5 flex items-center justify-center">{c.unread}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ChatView({ cid, onBack, onChanged }) {
  const { user } = useAuth();
  const [conn, setConn] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [c, m] = await Promise.all([api.get(`/connections/${cid}`), api.get(`/connections/${cid}/messages`)]);
      setConn(c.data); setMessages(m.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [cid]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    try { const { data } = await api.post(`/connections/${cid}/messages`, { text: t }); setMessages((m) => [...m, data]); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (status) => {
    try { await api.patch(`/connections/${cid}/status`, { status }); toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}`); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const submitReview = async () => {
    try { await api.post(`/connections/${cid}/review`, { rating, comment }); setReviewOpen(false); toast.success("Thank you for your review!"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!conn) return <div className="h-[70vh] rounded-2xl bg-white/60 border-2 border-border animate-pulse" />;

  return (
    <div className="bg-white rounded-2xl border-2 border-border shadow-sm flex flex-col h-[78vh]">
      <div className="flex items-center gap-3 p-4 border-b-2 border-border">
        <button onClick={onBack} className="lg:hidden text-forest-700" data-testid="chat-back"><ArrowLeft size={24} strokeWidth={2.5} /></button>
        <Avatar user={conn.other} size={46} />
        <div className="min-w-0">
          <p className="font-bold text-forest-900 truncate">{conn.other?.name}</p>
          <p className="text-sm text-forest-500 truncate">{conn.post?.title}</p>
        </div>
        <span className={`ml-auto px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLE[conn.status]}`}>{STATUS_LABEL[conn.status]}</span>
      </div>

      <div className="flex gap-2 px-4 py-2.5 border-b border-border overflow-x-auto">
        {["in_progress", "completed"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`chat-status-${s}`}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 whitespace-nowrap transition-colors ${conn.status === s ? "bg-forest-900 text-white border-forest-900" : "bg-white text-forest-700 border-border"}`}>
            Mark {STATUS_LABEL[s].toLowerCase()}
          </button>
        ))}
        {conn.status === "completed" && (
          <button onClick={() => setReviewOpen(true)} data-testid="leave-review-btn"
            className="px-3 py-1.5 rounded-full text-sm font-bold border-2 bg-gold text-forest-900 border-gold whitespace-nowrap flex items-center gap-1.5">
            <Star size={15} strokeWidth={2.5} /> Leave a review
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
        {messages.length === 0 && <p className="text-center text-forest-500 mt-8">Say hello 👋</p>}
        {messages.map((m) => {
          const mine = m.sender_id === user.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine ? "bg-terracotta text-white rounded-br-sm" : "bg-cream text-forest-900 border border-border rounded-bl-sm"}`}>
                <p className="text-lg leading-snug whitespace-pre-wrap break-words">{m.text}</p>
                <p className={`text-xs mt-1 ${mine ? "text-white/70" : "text-forest-500"}`}>{timeAgo(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="p-3 border-t-2 border-border flex items-center gap-2">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…"
          className="flex-1 min-h-[52px] text-lg px-4 rounded-full border-2 border-border focus:border-terracotta focus:ring-4 focus:ring-terracotta/20 outline-none bg-cream" />
        <button type="submit" data-testid="chat-send" className="w-[52px] h-[52px] shrink-0 rounded-full bg-terracotta text-white flex items-center justify-center hover:bg-terracotta-dark transition-transform active:scale-95">
          <Send size={22} strokeWidth={2.5} />
        </button>
      </form>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="bg-white border-2 border-border rounded-2xl">
          <DialogHeader><DialogTitle className="font-head text-2xl text-forest-900">Rate your experience with {conn.other?.name?.split(" ")[0]}</DialogTitle></DialogHeader>
          <div className="flex justify-center py-2"><StarRating value={rating} editable size={40} onChange={setRating} /></div>
          <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
            placeholder="Share a few kind words (optional)" className="w-full text-lg p-4 rounded-xl border-2 border-border focus:border-terracotta outline-none bg-cream resize-none" />
          <button onClick={submitReview} data-testid="review-submit" className="min-h-[56px] rounded-full bg-terracotta text-white font-bold text-lg hover:bg-terracotta-dark transition-colors flex items-center justify-center gap-2">
            <CheckCircle2 size={22} strokeWidth={2.5} /> Submit review
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Messages() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [conns, setConns] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/connections"); setConns(data); } catch {}
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  return (
    <div>
      <h1 className="font-head text-3xl sm:text-4xl font-bold text-forest-900 mb-6">Messages</h1>
      <div className="grid lg:grid-cols-[360px_1fr] gap-5">
        <div className={cid ? "hidden lg:block" : "block"}>
          <ConversationList conns={conns} activeId={cid} onPick={(id) => navigate(`/app/messages/${id}`)} />
        </div>
        <div className={cid ? "block" : "hidden lg:flex lg:items-center lg:justify-center"}>
          {cid ? (
            <ChatView cid={cid} onBack={() => navigate("/app/messages")} onChanged={load} />
          ) : (
            <div className="bg-white rounded-2xl border-2 border-border p-12 text-center w-full">
              <MessageCircle size={48} className="mx-auto text-forest-500 mb-4" strokeWidth={2} />
              <p className="text-forest-700 text-lg">Pick a conversation to start chatting.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
